//@flow
import * as Immutable from 'immutable';

import { PermanentJourneyPlan } from 'common/models/flow/PermanentJourneyPlan';
import type { PermanentJourneyPlanModelType } from 'common/models/flow/PermanentJourneyPlan';

import { TeamId } from './TeamId';
import { PlanId } from './PlanId';
import { OutletsInfo } from './OutletsInfo';
import { RidersInfo } from './RidersInfo';
import { PjpSummary } from './PjpSummary';
import { FpSalesbeatPlan } from './FpSalesbeatPlan';

import { PjPlanStatus } from './PjPlanStatus';

import { Progress } from './Progress';
import NumberUtils from 'common/utils/NumberUtils';

import { AuditMetadata } from 'common/models/flow/AuditMetadata';

import { PjPlanConfiguration } from 'common/models/flow/PjPlanConfiguration';

export const PjpPlanStatus = {
    Received: 'RECEIVED',
    Planning: 'PLANNING',
    Scheduling: 'SCHEDULING',
    Completed: 'COMPLETED',
    Error: 'ERROR',
    Frozen: 'FROZEN',
    Finalised: 'FINALISED' // FINALISED - added just for UI display. No such value exists in BE.
};

export const TerminalStatuses = [
    PjpPlanStatus.Completed,
    PjpPlanStatus.Error,
    PjpPlanStatus.Frozen
];

export const AllowedStatuses = [
    ...TerminalStatuses,
    PjpPlanStatus.Received,
    PjpPlanStatus.Planning
];

class InternalPermanentJourneyPlan extends PermanentJourneyPlan {
    static fromJS(
        json: PermanentJourneyPlanModelType
    ): InternalPermanentJourneyPlan {
        const state: Object = Object.assign({}, json);
        state.teamId = TeamId.fromJS(state.teamId);
        state.outletsInfo = OutletsInfo.fromJS(state.outletsInfo);
        state.ridersInfo = RidersInfo.fromJS(state.ridersInfo);
        state.plan = PlanId.fromJS(state.plan);
        state.existingPlan = PlanId.fromJS(state.existingPlan);
        state.distributionPlan = PlanId.fromJS(state.distributionPlan);
        state.planSummary = PjpSummary.fromJS(state.planSummary);
        state.customFields = Immutable.Map(state.customFields);
        state.customProperties = Immutable.Map(state.customProperties);
        state.statusUpdates = Immutable.List(state.statusUpdates).map(item =>
            PjPlanStatus.fromJS(item)
        );
        state.progress = Immutable.List(state.progress).map(item =>
            Progress.fromJS(item)
        );
        state.notes = state.notes ? Immutable.List(state.notes) : state.notes;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.configuration = state.configuration
            ? PjPlanConfiguration.fromJS(state.configuration)
            : state.configuration;
        state.salesbeatPlan = state.salesbeatPlan
            ? FpSalesbeatPlan.fromJS(state.salesbeatPlan)
            : state.salesbeatPlan;
        return new this(Immutable.Map(state));
    }

    getStatusUpdatesOf(planStatus: string) {
        return this.statusUpdates.find(
            statusUpdate => statusUpdate.status === planStatus
        );
    }

    getCreatedOnTime() {
        const relevantStatusUpdate = this.getStatusUpdatesOf('RECEIVED');
        return relevantStatusUpdate && relevantStatusUpdate.receiveTime;
    }

    getEffectiveStatus(): string {
        if (AllowedStatuses.includes(this.planStatus)) {
            if (this.planStatus === PjpPlanStatus.Frozen) {
                return PjpPlanStatus.Finalised;
            }
            return this.planStatus;
        }
        return PjpPlanStatus.Planning;
    }

    isInProgress(): boolean {
        return !TerminalStatuses.includes(this.planStatus);
    }

    isErrored(): boolean {
        return this.planStatus === PjpPlanStatus.Error;
    }

    isSame(pjp: InternalPermanentJourneyPlan): boolean {
        return pjp.clientId === this.clientId && pjp.id === this.id;
    }

    getProgressPercentage(): number {
        return NumberUtils.roundOff(
            this.progress.reduce(
                (currentValue, nextProgress) =>
                    currentValue + nextProgress.value,
                0
            ) / this.progress.size
        );
    }

    get outletsInfo(): OutletsInfo {
        return this._state.get('outletsInfo');
    }

    get ridersInfo(): RidersInfo {
        return this._state.get('ridersInfo');
    }

    get plan(): PlanId {
        return this._state.get('plan');
    }

    get existingPlan(): PlanId {
        return this._state.get('existingPlan');
    }

    get distributionPlan(): PlanId {
        return this._state.get('distributionPlan');
    }

    get planSummary(): PjpSummary {
        return this._state.get('planSummary');
    }

    get statusUpdates(): Immutable.List<PjPlanStatus> {
        return this._state.get('statusUpdates');
    }

    get teamId(): TeamId {
        return this._state.get('teamId');
    }

    get progress(): Immutable.List<Progress> {
        return this._state.get('progress');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get salesbeatPlan(): ?FpSalesbeatPlan {
        return this._state.get('salesbeatPlan');
    }
}

export { InternalPermanentJourneyPlan as PermanentJourneyPlan };
